import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { buddyLine, type BuddyMoment } from '@qq/shared';
import type { ActivityQuestion, AnswerResult, ActivityPrompt } from '@qq/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { submitAnswer } from '../lib/attempt-queue.js';
import { useSession } from '../lib/session.js';
import { canSpeak, cancelSpeech, sayAloud, useReadAloud } from '../lib/speech.js';
import { reloadIfUpdateWaiting } from '../lib/update.js';
import { Companion } from '../art/Companion.js';
import { Collectible } from '../art/Collectible.js';
import { FreeWritingCanvas, LetterTrail, OrderItems, type Point } from './writing-prompts.js';
import { ColumnSum } from './column-sum.js';
import { NumberPad } from './number-pad.js';
import { StackedSum } from './stacked-sum.js';

type Phase = 'asking' | 'checking' | 'feedback';

/**
 * One activity, start to finish.
 *
 * The feedback design is the whole point of this screen. A wrong answer gets a
 * warm line, the option to try again, and no red, no cross and no sound of
 * failure. Nothing already earned is taken away. The child who this protects is
 * the one who stops trying when she gets something wrong.
 */
export function Activity() {
  const navigate = useNavigate();
  const session = useSession();
  const child = session.data?.child;

  const [phase, setPhase] = useState<Phase>('asking');
  const [result, setResult] = useState<AnswerResult | 'queued' | null>(null);
  const [hintShown, setHintShown] = useState(false);
  const [isRetry, setIsRetry] = useState(false);
  const [tries, setTries] = useState(0);
  const [chosen, setChosen] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  // Set once the child asks for more. Every extra still needs a tap to reach -
  // nothing auto-advances - but they are not asked to opt in over and over.
  const [keepGoing, setKeepGoing] = useState(false);
  const [extrasDone, setExtrasDone] = useState(0);
  const shownAt = useRef<number>(Date.now());
  const [sessionId, setSessionId] = useState<string | null>(null);

  const question = useQuery({
    queryKey: ['activity', 'next', done, keepGoing],
    queryFn: () =>
      api<ActivityQuestion>(`/child/activities/next${keepGoing ? '?keepGoing=true' : ''}`),
    enabled: Boolean(child),
    /*
     * A question is never reusable.
     *
     * This was `staleTime: Infinity`, and `done` counts up only WITHIN one
     * mount - so walking to the shelf and back reset it to 0 and React Query
     * handed back the question it had cached on the first mount. Aubrey
     * answered 48 - 32 six times across eight minutes, every one carrying the
     * SAME questionId, which is how we know the server was never asked: it
     * mints a fresh uuid per request. The repeat-avoidance added for exactly
     * this was sitting behind a cache it could not see past.
     *
     * gcTime 0 as well as staleTime 0: a stale entry that is merely refetched
     * still renders the old question for a frame, and a child who sees the
     * answer they just gave will tap it.
     */
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });

  useEffect(() => {
    if (!child || sessionId) return;
    void api<{ sessionId: string }>('/child/sessions', { method: 'POST' }).then((s) =>
      setSessionId(s.sessionId),
    );
  }, [child, sessionId]);

  useEffect(() => {
    shownAt.current = Date.now();
    setPhase('asking');
    setResult(null);
    setChosen(null);
    setHintShown(false);
  }, [question.data?.questionId]);

  useReadAloud(
    question.data?.questionId,
    question.data?.speak,
    child?.readAloud ?? 'tap',
    phase === 'asking',
  );

  const missionDone =
    question.error instanceof ApiRequestError && question.error.body.code === 'mission_complete';
  useEffect(() => {
    if (missionDone) void reloadIfUpdateWaiting();
  }, [missionDone]);

  if (session.isPending) return <main className="screen">Just a moment…</main>;
  if (!child) return <Navigate to="/" replace />;

  const answer = async (
    choiceId: string,
    extra: { traced?: Point[][]; writing?: { text?: string; drawing?: string } } = {},
  ) => {
    if (!question.data || phase !== 'asking') return;
    setChosen(choiceId);
    setPhase('checking');

    const outcome = await submitAnswer({
      attemptId: crypto.randomUUID(),
      token: question.data.token,
      choiceId,
      ...(extra.traced ? { traced: extra.traced } : {}),
      ...(extra.writing ? { writing: extra.writing } : {}),
      assistLevel: isRetry ? 'retry' : hintShown ? 'hint' : 'independent',
      latencyMs: Math.min(600_000, Date.now() - shownAt.current),
      ...(sessionId ? { sessionId } : {}),
    }).catch((err) => {
      if (err instanceof ApiRequestError) return 'queued' as const;
      throw err;
    });

    setResult(outcome);
    setPhase('feedback');
  };

  const tryAgain = () => {
    setIsRetry(true);
    // A fresh go: the prompt is remounted, so a traced letter, a half-built
    // word or a sorted order starts empty instead of locked as it was left.
    setTries((n) => n + 1);
    setPhase('asking');
    setResult(null);
    setChosen(null);
    shownAt.current = Date.now();
  };

  /**
   * Leaving.
   *
   * This used to just navigate home, so the session row stayed open forever
   * and was resumed on every future visit - which pinned the child to the
   * first mission they ever had. Ending it is the whole fix.
   *
   * Fire-and-forget on purpose: a child tapping "Finish for now" must never
   * wait on the network, and the server closes stale sessions anyway.
   */
  const finish = () => {
    if (sessionId) {
      void api(`/child/sessions/${sessionId}/end`, { method: 'POST' }).catch(() => undefined);
    }
    navigate('/home');
  };

  const nextQuestion = async () => {
    // Between questions: the answer is in, the next one is not on screen yet.
    // If a new version is waiting, this is when to pick it up.
    if (await reloadIfUpdateWaiting()) return;
    setIsRetry(false);
    if (question.data?.extra) setExtrasDone((n) => n + 1);
    setDone((n) => n + 1);
  };

  // Not until THIS visit's question has arrived. A question still in the
  // cache from the last visit would otherwise be drawn first, and a child
  // who taps it answers a question they have already had.
  if (question.isPending || (question.isFetching && !question.isFetchedAfterMount)) {
    return (
      <main className="screen">
        <Companion companionKey={child.companionId} size={120} />
        <p>Getting your next adventure ready…</p>
      </main>
    );
  }

  if (question.isError || !question.data) {
    const finished =
      question.error instanceof ApiRequestError && question.error.body.code === 'mission_complete';

    // Two genuinely different things, and they must not share a screen: one is
    // "well done, you finished", the other is "something is not set up".
    return (
      <main className="screen">
        <Companion companionKey={child.companionId} size={120} />
        {finished ? (
          <>
            <h1>That&rsquo;s today&rsquo;s adventure done!</h1>
            <p>
              {extrasDone > 0
                ? 'You have gone past the end already. Keep going as long as you like.'
                : 'You can stop here, or keep exploring for as long as you want.'}
            </p>
            <button
              type="button"
              className="btn btn--big"
              onClick={() => {
                setKeepGoing(true);
                setDone((n) => n + 1);
              }}
            >
              Keep exploring
            </button>

            {/* The other way a game is reached. The breather after a run of
                misses was the only route, which meant the only way to a game
                was to struggle - and once exploring started rotating skills,
                even that stopped firing. Finishing the adventure is the
                ordinary route, and the one that makes sure failing is never
                the way in. */}
            <BreatherAtTheEnd />
          </>
        ) : (
          <>
            <h1>Nothing to practise just yet</h1>
            <p>Your grown-up is still setting up your quest. Come back soon!</p>
          </>
        )}
        <button type="button" className="btn btn--quiet" onClick={finish}>
          {finished ? 'Finish for now' : 'Back home'}
        </button>
      </main>
    );
  }

  const q = question.data;
  const correct = result !== null && result !== 'queued' && result.correct;
  const queued = result === 'queued';

  return (
    <main className="screen activity">
      <div className="activity-top">
        <Companion companionKey={child.companionId} size={72} />
        {child.buddy && (
          <Collectible collectibleKey={child.buddy.key} size={52} title={child.buddy.name} />
        )}
        <span className="pill">{q.skillName}</span>
        {q.extra && <span className="pill pill--ok">Extra</span>}
        {q.speak && child.readAloud !== 'off' && canSpeak() && (
          <button
            type="button"
            className="speak-button"
            aria-label="Hear the question"
            onClick={() => q.speak && sayAloud(q.speak)}
          >
            🔊
          </button>
        )}
      </div>

      <Prompt
        key={`${q.questionId}:${tries}`}
        prompt={q.prompt}
        onChoose={(id, extra) => void answer(id, extra ?? {})}
        disabled={phase !== 'asking'}
        chosen={chosen}
      />

      {phase === 'asking' && !hintShown && (
        <button type="button" className="btn btn--quiet" onClick={() => setHintShown(true)}>
          Show me a clue
        </button>
      )}
      {hintShown && phase === 'asking' && <p className="gentle-note">{q.hint}</p>}

      {phase === 'checking' && <p className="muted">Checking…</p>}

      {phase === 'feedback' && (
        <div className="feedback">
          {queued ? (
            <p className="gentle-note">
              Saved! We&rsquo;ll check that one when the internet comes back. Keep going.
            </p>
          ) : (
            <p className={correct ? 'praise-note' : 'gentle-note'}>
              {(result as AnswerResult).feedback}
            </p>
          )}

          {!queued && child.buddy && (
            <BuddyCheer
              buddy={child.buddy}
              moment={
                correct ? 'right' : (result as AnswerResult).retryOffered ? 'tryAgain' : 'moveOn'
              }
              seed={`${q.questionId}:${tries}`}
              speak={child.readAloud === 'auto'}
            />
          )}

          {!queued && !correct && (
            <CoachCard
              result={result as AnswerResult}
              hint={q.hint}
              companionKey={child.companionId}
            />
          )}

          {/* Outside the coach card on purpose. It used to live inside, and
              the coach card only appears when the answer is WRONG - so the
              "twelve right in a row" offer was made by the server and had
              nowhere to render. Aubrey earned a game and never saw one. */}
          {!queued && <BreatherOffer result={result as AnswerResult} />}

          {!queued && !correct && !(result as AnswerResult).retryOffered && (
            <p className="answer-reveal">
              The answer was <b>{(result as AnswerResult).correctChoiceId}</b>.
            </p>
          )}

          {!queued && !correct && (result as AnswerResult).retryOffered ? (
            <button type="button" className="btn" onClick={tryAgain}>
              Try again
            </button>
          ) : (
            <button type="button" className="btn" onClick={() => void nextQuestion()}>
              Next one
            </button>
          )}
        </div>
      )}

      <button type="button" className="btn btn--quiet" onClick={finish}>
        Finish for now
      </button>
    </main>
  );
}

/**
 * The buddy's turn: a short line after every answer, and a little bounce when
 * it was right. Not tied to stars, the ladder or whether the answer counted -
 * a child who repeats a question does not know it was a repeat, and silence
 * would read as a slight.
 *
 * Said aloud only for a child whose questions are read to them, once, after
 * the feedback is on screen. The one thing spoken during feedback, and it
 * holds no numbers and no words from the question.
 */
function BuddyCheer({
  buddy,
  moment,
  seed,
  speak,
}: {
  buddy: { key: string; name: string };
  moment: BuddyMoment;
  seed: string;
  speak: boolean;
}) {
  const line = buddyLine(moment, seed);
  useEffect(() => {
    if (speak) sayAloud({ text: `${buddy.name} says: ${line}` });
  }, [speak, buddy.name, line]);
  return (
    <div className="buddy" data-cheer={moment === 'right'}>
      <Collectible collectibleKey={buddy.key} size={56} title={buddy.name} />
      <p className="buddy-bubble">
        {buddy.name}: {line}
      </p>
    </div>
  );
}

/**
 * What the child is shown after a miss.
 *
 * Three rungs of the intervention ladder land here, and they differ only in
 * how much is given without being asked for:
 *
 *   none              the line, and "Show me how" if there are steps to show
 *   easier-with-hint  the line plus the question's own clue, unprompted
 *   worked-example    every step opened, because two misses in a row means
 *                     the child has stopped being able to find it themselves
 *
 * The fourth rung never reaches here - the server has already stopped offering
 * the retry, and the screen moves on.
 *
 * Steps appear one at a time and STAY, rather than replacing each other. The
 * method is a sequence, and a child who has lost their place needs to see the
 * step before the one they are on.
 */
function CoachCard({
  result,
  hint,
  companionKey,
}: {
  result: AnswerResult;
  hint: string;
  companionKey: string | null;
}) {
  const worked = result.intervention === 'worked-example';
  const coach = result.coach;
  const [shown, setShown] = useState(worked ? (coach?.steps.length ?? 0) : 0);

  if (result.intervention === 'drop-to-prerequisite') {
    return (
      <div className="coach coach--move-on">
        <Companion companionKey={companionKey} size={64} />
        <p className="coach__line">
          That one is being tricky today, and that is completely fine. Let&rsquo;s do something else
          and come back to it another day.
        </p>
      </div>
    );
  }

  if (!coach) return null;

  const steps = coach.steps.slice(0, shown);
  const more = shown < coach.steps.length;

  return (
    <div className="coach">
      <div className="coach__head">
        <Companion companionKey={companionKey} size={64} />
        <p className="coach__line">{coach.line}</p>
      </div>

      {result.intervention === 'easier-with-hint' && <p className="coach__hint">{hint}</p>}

      {steps.length > 0 && (
        <ol className="coach__steps">
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}

      {more && (
        <button type="button" className="btn btn--outline" onClick={() => setShown(shown + 1)}>
          {shown === 0 ? 'Show me how' : 'Then what?'}
        </button>
      )}
    </div>
  );
}

/**
 * A game at the end of the adventure.
 *
 * Only shown when one is actually available - it asks the server, which
 * answers with nothing once they have had their game for the day.
 */
function BreatherAtTheEnd() {
  const navigate = useNavigate();
  const offer = useQuery({
    queryKey: ['games', 'offer'],
    queryFn: () =>
      api<{ available: { key: string; name: string; invitation: string }[] }>('/child/games/offer'),
    staleTime: 0,
  });

  const game = offer.data?.available?.[0];
  if (!game) return null;

  return (
    <button type="button" className="btn btn--outline" onClick={() => navigate('/games')}>
      {game.invitation}
    </button>
  );
}

/**
 * Stand up, not try harder.
 *
 * Three misses in a row is a moment to stretch, and the companion asks rather
 * than decides. Declining costs nothing and is not asked again.
 */
function BreatherOffer({ result }: { result: AnswerResult }) {
  const navigate = useNavigate();
  if (!result.breather) return null;

  return (
    <button type="button" className="btn btn--outline" onClick={() => navigate('/games')}>
      {result.breather.invitation}
    </button>
  );
}

/**
 * Exported for the question audit sheet, which server-renders every prompt
 * the app can produce so a human can look at them all at once. The bugs that
 * reached the children were visible ones that a green suite never caught.
 */
export function Prompt({
  prompt,
  onChoose,
  disabled,
  chosen,
}: {
  prompt: ActivityPrompt;
  onChoose: (
    id: string,
    extra?: { traced?: Point[][]; writing?: { text?: string; drawing?: string } },
  ) => void;
  disabled: boolean;
  chosen: string | null;
}) {
  // Handled before `choices` is touched: column addition is the one prompt with
  // no list to pick from, so narrowing it away here keeps the rest simple.
  if (prompt.kind === 'columnAddition') {
    return (
      <>
        <h1>{prompt.instruction}</h1>
        <ColumnSum
          key={`${prompt.top}+${prompt.bottom}`}
          top={prompt.top}
          bottom={prompt.bottom}
          answerCells={prompt.answerCells}
          disabled={disabled}
          onSubmit={(value) => onChoose(value)}
        />
      </>
    );
  }

  // Shapes get drawn. As words, "rectangle" and "square" are a reading
  // question, and on the contact sheet the rectangle was the size of a hyphen.
  const asShapes = prompt.kind === 'pickAnswer' && prompt.shapes === true;

  const choices = (
    <div className="choice-row" data-shapes={asShapes ? true : undefined}>
      {prompt.choices.map((choice) => (
        <button
          key={choice.id}
          type="button"
          className={asShapes ? 'choice choice--shape' : 'choice'}
          data-chosen={chosen === choice.id}
          disabled={disabled}
          onClick={() => onChoose(choice.id)}
        >
          {asShapes && <ShapeGlyph name={choice.id} />}
          {choice.label && (
            <span className={asShapes ? 'choice__name' : undefined}>{choice.label}</span>
          )}
        </button>
      ))}
    </div>
  );

  if (prompt.kind === 'countAndChoose') {
    return (
      <>
        <h1>{prompt.instruction}</h1>
        {prompt.layout === 'subitise' ? (
          // No counting-field wrapper: the pattern draws its own box, and
          // nesting them put a border inside a border.
          <DotPattern count={prompt.count} glyph={prompt.itemGlyph} />
        ) : (
          <CountingField count={prompt.count} glyph={prompt.itemGlyph} />
        )}
        {choices}
      </>
    );
  }

  if (prompt.kind === 'blendChoose') {
    return (
      <>
        <h1>{prompt.instruction}</h1>
        <div className="grapheme-row">
          {prompt.graphemes.map((g, i) => (
            <span key={`${g}-${i}`} className="grapheme-tile">
              {g}
            </span>
          ))}
        </div>
        {choices}
      </>
    );
  }

  if (prompt.kind === 'pickAnswer') {
    return (
      <>
        <h1>{prompt.instruction}</h1>
        {/* The objects BEFORE the equation. Concrete, then abstract - a child
            who can see six things and one of them showing does not need to
            read "6 = 1 + ?" to start, and the picture is what the written form
            is a record of. */}
        {prompt.grid && <GlyphGrid {...prompt.grid} />}
        {prompt.display && <p className="maths-display">{prompt.display}</p>}
        {prompt.money && (
          <div className="money-row">
            {prompt.money.map((coin, i) => (
              <span key={`${coin}-${i}`} className="coin">
                {coin}
              </span>
            ))}
          </div>
        )}
        {prompt.scene && <PositionScene {...prompt.scene} />}
        {prompt.compare && <CompareScene {...prompt.compare} />}
        {prompt.clock && <ClockFace hour={prompt.clock.hour} minute={prompt.clock.minute} />}
        {prompt.graph && (
          <div className="picture-graph">
            {prompt.graph.map((row) => (
              <div key={row.label} className="graph-row">
                <span className="graph-label">{row.label}</span>
                <span aria-label={`${row.count}`}>{row.glyph.repeat(row.count)}</span>
              </div>
            ))}
          </div>
        )}
        {/* A written sum is worth SEEING stacked long before a child is ready
            to type the answer, and the carry box is useful either way. */}
        {prompt.stacked && prompt.answerFormat !== 'keypad' && (
          <StackedSum
            top={prompt.stacked.top}
            bottom={prompt.stacked.bottom}
            operator={prompt.stacked.operator}
            answerCells={prompt.stacked.answerCells}
          />
        )}
        {prompt.answerFormat === 'keypad' && prompt.stacked ? (
          // A written sum gets columns and carry boxes rather than one line.
          <ColumnSum
            top={prompt.stacked.top}
            bottom={prompt.stacked.bottom}
            operator={prompt.stacked.operator}
            answerCells={prompt.stacked.answerCells}
            disabled={Boolean(disabled)}
            onSubmit={(answer) => onChoose(answer)}
          />
        ) : prompt.answerFormat === 'keypad' ? (
          <NumberPad disabled={disabled} onSubmit={(answer) => onChoose(answer)} />
        ) : (
          choices
        )}
      </>
    );
  }

  if (prompt.kind === 'storyBook') {
    return <StoryBook prompt={prompt} choices={choices} />;
  }

  if (prompt.kind === 'traceLetter') {
    return (
      <LetterTrail
        prompt={prompt}
        disabled={disabled}
        onSubmit={(id, traced) => onChoose(id, { traced })}
      />
    );
  }

  if (prompt.kind === 'orderItems') {
    return <OrderItems prompt={prompt} disabled={disabled} onSubmit={(id) => onChoose(id)} />;
  }

  if (prompt.kind === 'freeWriting') {
    return (
      <FreeWritingCanvas
        prompt={prompt}
        disabled={disabled}
        onSubmit={(id, writing) => onChoose(id, { writing })}
      />
    );
  }

  if (prompt.kind === 'buildWord') {
    return <WordBuilder prompt={prompt} onSubmit={onChoose} disabled={disabled} />;
  }

  return (
    <>
      <h1>{prompt.instruction}</h1>
      <ClipPlayer audioKey={prompt.audioKey} label="Listen again" />
      {choices}
    </>
  );
}

/**
 * Ten frames, arrays and equal groups.
 *
 * `frame` draws it as a real ten frame: every cell outlined, five to a row,
 * with the rows of five visibly separated. Without that it was a loose row of
 * dots, so "the top row holds five" - the entire reason a ten frame exists,
 * and exactly what the hint tells the child to use - was not true of what was
 * on screen.
 *
 * `hidden` says the cells that are not filled hold something HIDDEN rather
 * than nothing. Part-whole asks "how many are hiding", and an empty cell reads
 * as space left over.
 */
function GlyphGrid({
  rows,
  cols,
  glyph,
  filled,
  frame,
  hidden,
}: {
  rows: number;
  cols: number;
  glyph: string;
  filled?: number;
  frame?: boolean;
  hidden?: boolean;
}) {
  const total = rows * cols;
  const cell = (i: number) => {
    const full = filled === undefined || i < filled;
    return (
      <span
        key={i}
        className="glyph-cell"
        data-filled={full}
        data-hidden={!full && hidden ? true : undefined}
      >
        {full ? glyph : hidden ? '?' : ''}
      </span>
    );
  };

  /*
   * A twenty frame is TWO ten frames, not a block four rows deep.
   *
   * That is how it is taught and how it is drawn in the classroom: fill the
   * first frame, then start the second, so "thirteen" is seen as a full ten
   * and three more. Four rows in one box is just a grid, and the ten stops
   * being the thing the child can see.
   */
  if (frame && rows > 2) {
    const perFrame = cols * 2;
    const frames = Math.ceil(total / perFrame);
    return (
      <div className="frame-stack" aria-label={`${filled ?? total} of ${total}`}>
        {Array.from({ length: frames }, (_, f) => (
          <div
            key={f}
            className="glyph-grid"
            data-frame={true}
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: Math.min(perFrame, total - f * perFrame) }, (_, i) =>
              cell(f * perFrame + i),
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="glyph-grid"
      data-frame={frame ? true : undefined}
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      aria-label={`${filled ?? total} of ${total}`}
    >
      {Array.from({ length: total }, (_, i) => cell(i))}
    </div>
  );
}

/**
 * Two or three things placed in a grid, for position words.
 *
 * "Where is the cat?" cannot be asked in words - it needs a cat somewhere.
 * The skill was hinted at shape-sorter, which has no position mode, so it
 * accrued mastery for naming shapes instead.
 */
function PositionScene({
  rows,
  cols,
  items,
}: {
  rows: number;
  cols: number;
  items: { row: number; col: number; glyph: string; label: string; subject?: boolean }[];
}) {
  const described = items
    .map((item) => `${item.label} at row ${item.row}, column ${item.col}`)
    .join('; ');

  return (
    <div
      className="position-scene"
      style={{
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
      }}
      role="img"
      aria-label={described}
    >
      {items.map((item) => (
        <span
          key={`${item.row}-${item.col}-${item.label}`}
          className="position-scene__item"
          data-subject={item.subject ? true : undefined}
          style={{ gridRow: item.row, gridColumn: item.col }}
          aria-hidden="true"
        >
          {item.glyph}
        </span>
      ))}
    </div>
  );
}

/**
 * Two things compared by an attribute you can see.
 *
 * Length is drawn as two bars from the same starting line, because lining
 * them up at one end is the actual method. Mass is the two things side by
 * side - a tipped balance gave the answer away. Capacity is two containers
 * with what they hold drawn inside them.
 *
 * Both skills used to be hinted at compare-order, which compares NUMERALS -
 * so "which is longer" was answered by "which number is bigger".
 */
function CompareScene({
  attribute,
  left,
  right,
}: {
  attribute: 'length' | 'mass' | 'capacity';
  left: { glyph: string; label: string; size: number };
  right: { glyph: string; label: string; size: number };
}) {
  const label = `${left.label} and ${right.label}, compared by ${attribute}`;

  if (attribute === 'length') {
    return (
      <div className="compare-scene compare-scene--length" role="img" aria-label={label}>
        {[left, right].map((thing, i) => (
          <div key={i} className="compare-bar-row">
            <span className="compare-bar__face" aria-hidden="true">
              {thing.glyph}
            </span>
            <span className="compare-bar" style={{ width: `${thing.size * 10}%` }} />
            <span className="compare-bar__name">{thing.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (attribute === 'mass') {
    /*
     * Side by side, the same size, and no balance.
     *
     * The balance tipped towards the heavier side, so "Which one is heavier?"
     * was answered by the picture - read which side is down, know nothing
     * about bags or feathers. A level balance would be worse: it says they
     * weigh the same. Nothing on screen hints at the answer; the child has to
     * know.
     */
    return (
      <div className="compare-scene compare-scene--mass" role="img" aria-label={label}>
        {[left, right].map((thing, i) => (
          <div key={i} className="compare-mass">
            <span className="compare-mass__face" aria-hidden="true" style={{ fontSize: '3.5rem' }}>
              {thing.glyph}
            </span>
            <span className="compare-mass__name">{thing.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="compare-scene compare-scene--capacity" role="img" aria-label={label}>
      {/* The CONTAINER grows, and both are full.
          Drawing two same-sized jars with different fill levels shows how
          much is IN them, which is a different question - "holds more" is
          about the container, so the container is what has to differ. */}
      {[left, right].map((thing, i) => (
        <div key={i} className="compare-jar">
          <span className="compare-jar__face" aria-hidden="true">
            {thing.glyph}
          </span>
          <span
            className="compare-jar__body"
            style={{
              height: `${40 + thing.size * 9}px`,
              width: `${30 + thing.size * 5}px`,
            }}
          >
            <span className="compare-jar__fill" style={{ height: '100%' }} />
          </span>
          <span className="compare-bar__name">{thing.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The dice and pair patterns, so a number has a SHAPE.
 *
 * Six dots in a straight line is a counting question wearing a subitising
 * question's name. These are the arrangements a child recognises without
 * counting, which is the whole skill - and they are the ones on a die, so most
 * children have met them before they meet this app.
 */
const DICE_SPOTS: Record<number, [number, number][]> = {
  1: [[2, 2]],
  2: [
    [1, 1],
    [3, 3],
  ],
  3: [
    [1, 1],
    [2, 2],
    [3, 3],
  ],
  4: [
    [1, 1],
    [1, 3],
    [3, 1],
    [3, 3],
  ],
  5: [
    [1, 1],
    [1, 3],
    [2, 2],
    [3, 1],
    [3, 3],
  ],
  6: [
    [1, 1],
    [1, 3],
    [2, 1],
    [2, 3],
    [3, 1],
    [3, 3],
  ],
};

function DotPattern({ count, glyph }: { count: number; glyph: string }) {
  const spots = DICE_SPOTS[count];

  // Above six there is no single recognisable pattern, so it becomes two
  // groups a child can see as "five and some more".
  if (!spots) {
    const first = Math.min(5, count);
    return (
      <div className="dot-pattern dot-pattern--pair" aria-label={`${count} dots`}>
        <DotPattern count={first} glyph={glyph} />
        <DotPattern count={count - first} glyph={glyph} />
      </div>
    );
  }

  return (
    <div className="dot-pattern" aria-label={`${count} dots`}>
      {spots.map(([row, col]) => (
        <span
          key={`${row}-${col}`}
          className="dot-pattern__spot"
          style={{ gridRow: row, gridColumn: col }}
          aria-hidden="true"
        >
          {glyph}
        </span>
      ))}
    </div>
  );
}

/**
 * The shapes, drawn.
 *
 * As words these were a reading question rather than a shape question, and on
 * the contact sheet the rectangle rendered at the size of a hyphen. Drawn at
 * the app's own tap size, with the name kept underneath so the word is still
 * being learnt alongside the shape.
 */
function ShapeGlyph({ name }: { name: string }) {
  const common = { fill: 'var(--shape-fill, #cfe8ff)', stroke: 'var(--ink)', strokeWidth: 4 };
  return (
    <svg viewBox="0 0 100 100" className="shape-glyph" role="img" aria-label={name}>
      {name === 'circle' && <circle cx="50" cy="50" r="40" {...common} />}
      {name === 'square' && <rect x="12" y="12" width="76" height="76" rx="4" {...common} />}
      {name === 'rectangle' && <rect x="6" y="26" width="88" height="48" rx="4" {...common} />}
      {name === 'triangle' && <polygon points="50,12 92,88 8,88" {...common} />}
      {name === 'oval' && <ellipse cx="50" cy="50" rx="44" ry="28" {...common} />}
      {name === 'hexagon' && <polygon points="30,14 70,14 92,50 70,86 30,86 8,50" {...common} />}
      {/* The 3D ones get a drawn hint of depth, because that is the difference
          being asked about: a square is not a cube. */}
      {name === 'cube' && (
        <g {...common}>
          <polygon points="22,34 60,34 60,78 22,78" />
          <polygon points="22,34 40,16 78,16 60,34" />
          <polygon points="60,34 78,16 78,60 60,78" />
        </g>
      )}
      {name === 'sphere' && (
        <g {...common}>
          <circle cx="50" cy="50" r="38" />
          <ellipse cx="50" cy="50" rx="38" ry="13" fill="none" />
        </g>
      )}
      {name === 'cone' && (
        <g {...common}>
          <polygon points="50,12 82,76 18,76" />
          <ellipse cx="50" cy="76" rx="32" ry="12" />
        </g>
      )}
      {name === 'cylinder' && (
        <g {...common}>
          <rect x="18" y="22" width="64" height="56" />
          <ellipse cx="50" cy="22" rx="32" ry="12" />
          <ellipse cx="50" cy="78" rx="32" ry="12" />
        </g>
      )}
    </svg>
  );
}

/** An analogue clock. Drawn rather than described, because reading the hands
 *  is the skill - a digital readout would answer the question for them. */
function ClockFace({ hour, minute }: { hour: number; minute: number }) {
  const minuteAngle = minute * 6;
  const hourAngle = (hour % 12) * 30 + minute * 0.5;
  const hand = (angle: number, length: number, width: number) => {
    const radians = ((angle - 90) * Math.PI) / 180;
    return (
      <line
        x1="100"
        y1="100"
        x2={100 + Math.cos(radians) * length}
        y2={100 + Math.sin(radians) * length}
        stroke="#2a2e2d"
        strokeWidth={width}
        strokeLinecap="round"
      />
    );
  };

  return (
    <svg viewBox="0 0 200 200" width="200" height="200" role="img" aria-label="A clock">
      <circle cx="100" cy="100" r="92" fill="#fff" stroke="#2f7d6b" strokeWidth="6" />
      {Array.from({ length: 12 }, (_, i) => {
        const radians = ((i * 30 - 90) * Math.PI) / 180;
        return (
          <text
            key={i}
            x={100 + Math.cos(radians) * 72}
            y={100 + Math.sin(radians) * 72 + 7}
            textAnchor="middle"
            fontSize="18"
            fontWeight="700"
            fill="#2a2e2d"
          >
            {i === 0 ? 12 : i}
          </text>
        );
      })}
      {hand(hourAngle, 44, 8)}
      {hand(minuteAngle, 66, 5)}
      <circle cx="100" cy="100" r="6" fill="#2a2e2d" />
    </svg>
  );
}

/** Plays a curated clip. The server refuses to serve one nobody has checked,
 *  so an unaudited recording simply does not arrive. */
function ClipPlayer({ audioKey, label }: { audioKey: string; label: string }) {
  const [failed, setFailed] = useState(false);
  const base = (import.meta.env?.VITE_API_BASE as string | undefined) ?? '/api';

  if (failed) {
    return <p className="gentle-note">That sound is not ready yet. Ask a grown-up!</p>;
  }
  return (
    <audio
      src={`${base}/audio/${encodeURIComponent(audioKey)}`}
      controls
      className="phoneme-audio"
      aria-label={label}
      // A recorded sound and the iPad's voice never talk over each other.
      onPlay={cancelSpeech}
      onError={() => setFailed(true)}
    />
  );
}

/**
 * The mini-book. Every word on every page has already passed the decodability
 * validator for this child, so tapping any word to hear it is safe to offer -
 * it plays a recorded clip when one exists and stays silent when it does not,
 * rather than reading the word aloud in a voice nobody has checked.
 */
function StoryBook({
  prompt,
  choices,
}: {
  prompt: Extract<ActivityPrompt, { kind: 'storyBook' }>;
  choices: ReactNode;
}) {
  const [page, setPage] = useState(0);
  const [reading, setReading] = useState(true);
  const base = (import.meta.env?.VITE_API_BASE as string | undefined) ?? '/api';

  const current = prompt.pages[page];
  const lastPage = page >= prompt.pages.length - 1;

  const speak = (word: string) => {
    const clean = word.toLowerCase().replace(/[^a-z']/g, '');
    if (!clean) return;
    const audio = new Audio(`${base}/audio/${encodeURIComponent(`word.${clean}`)}`);
    // No clip, no sound. Deliberately not falling back to a synthetic voice.
    // And the voice reading the question stops, so the two never overlap.
    cancelSpeech();
    void audio.play().catch(() => undefined);
  };

  if (reading && current) {
    return (
      <>
        <h1>{prompt.title}</h1>
        <div className="book-page">
          <span className="book-glyph" aria-hidden="true">
            {current.glyph}
          </span>
          <p className="book-text">
            {current.text.split(/\s+/).map((word, i) => (
              <button
                key={`${word}-${i}`}
                type="button"
                className="book-word"
                onClick={() => speak(word)}
              >
                {word}
              </button>
            ))}
          </p>
        </div>
        <p className="muted">
          Page {page + 1} of {prompt.pages.length} · tap a word to hear it
        </p>
        <div className="selfie-actions">
          {page > 0 && (
            <button type="button" className="btn btn--outline" onClick={() => setPage(page - 1)}>
              Back
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => (lastPage ? setReading(false) : setPage(page + 1))}
          >
            {lastPage ? 'I have read it' : 'Next page'}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <h1>{prompt.question}</h1>
      {prompt.inference && (
        <p className="muted">The story does not say it straight out. What do you think?</p>
      )}
      <button type="button" className="btn btn--quiet" onClick={() => setReading(true)}>
        Read it again
      </button>
      {choices}
    </>
  );
}

/** Build a word from sound tiles. The assembled word is the answer. */
function WordBuilder({
  prompt,
  onSubmit,
  disabled,
}: {
  prompt: Extract<ActivityPrompt, { kind: 'buildWord' }>;
  onSubmit: (id: string) => void;
  disabled: boolean;
}) {
  const [slots, setSlots] = useState<string[]>([]);

  const place = (tile: string) => {
    if (slots.length >= prompt.slots) return;
    setSlots([...slots, tile]);
  };

  return (
    <>
      <h1>{prompt.instruction}</h1>
      {prompt.audioKey && <ClipPlayer audioKey={prompt.audioKey} label="Hear the word" />}

      <div className="grapheme-row">
        {Array.from({ length: prompt.slots }, (_, i) => (
          <span key={i} className="grapheme-tile grapheme-tile--slot">
            {slots[i] ?? ''}
          </span>
        ))}
      </div>

      <div className="grapheme-row">
        {prompt.tiles.map((tile, i) => (
          <button
            key={`${tile}-${i}`}
            type="button"
            className="grapheme-tile grapheme-tile--pick"
            disabled={disabled}
            onClick={() => place(tile)}
          >
            {tile}
          </button>
        ))}
      </div>

      <div className="selfie-actions">
        <button type="button" className="btn btn--outline" onClick={() => setSlots([])}>
          Start again
        </button>
        <button
          type="button"
          className="btn"
          disabled={disabled || slots.length !== prompt.slots}
          onClick={() => onSubmit(slots.join(''))}
        >
          That&rsquo;s my word
        </button>
      </div>
    </>
  );
}

/**
 * The things to be counted.
 *
 * Always visible, and there is deliberately no way to make them otherwise.
 *
 * This used to flash the collection for a second and hide it, for subitising.
 * The flash began the instant the question rendered - before a child had read
 * the question, let alone looked at the dots - so it tested reaction time and
 * luck rather than recognising a small quantity. It also leaked its hidden
 * state onto the next ordinary counting question, which then asked a child to
 * count an empty box.
 *
 * The timer, the state and the prompt field are all gone rather than merely
 * switched off, so it cannot come back by accident. Real subitising needs a
 * "ready, go" the child controls, not a surprise.
 */
export function CountingField({ count, glyph }: { count: number; glyph: string }) {
  return (
    <div className="counting-field" aria-label={`${count} things to count`}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="counting-item" aria-hidden="true">
          {glyph}
        </span>
      ))}
    </div>
  );
}
